/**
 * API-клиент «Блог МИНа» (devblog).
 * Бэкенд: storys-микросервис, /api-storys/kubikon3d/news/*
 *
 * Чтение — гость может всё. Действия (лайк/коммент/реакция) — нужен токен.
 * Писать новости — только МИН (user_id==1), бэкенд вернёт 403 остальным.
 */
import { request, API_BASE } from "./client";

const P = "/api-storys/kubikon3d/news";

/* ── Чтение ──────────────────────────────────────────────────────── */

/** Список карточек. opts: {category, page, limit} */
/* signal — чтобы вызывающий мог отменить запрос при смене рубрики.
   Без него быстрое переключение фильтров приводило к тому, что ответ
   предыдущей рубрики приходил позже и перезаписывал текущую выборку:
   под заголовком «Анонсы» оказывались статьи из «Обновлений». */
export function listNews({ category, page = 1, limit = 20 } = {}, { signal } = {}) {
  const qs = new URLSearchParams({ page, limit });
  if (category) qs.set("category", category);
  return request(`${P}?${qs.toString()}`, { auth: true, signal }); // auth опц.: бэк поймёт is_liked
}

/** Полная статья + инкремент просмотра. */
export function getNews(id) {
  return request(`${P}/${id}`, { auth: true });
}

/** Комментарии (дерево с ответами). sort: 'new'|'top' */
export function getComments(id, sort = "new") {
  return request(`${P}/${id}/comments?sort=${sort}`, { auth: true });
}

/** Сколько новостей пользователь ещё не видел (для бейджа в меню). */
export function getUnreadCount() {
  return request(`${P}/unread-count`, { auth: true });
}

/* ── Действия (нужен токен) ──────────────────────────────────────── */

/** Лайк/реакция (toggle). reaction: 'like'|'heart'|'fire'|'party' */
export function toggleLike(id, reaction = "like") {
  return request(`${P}/${id}/like`, { method: "POST", body: { reaction }, auth: true });
}

/** Добавить комментарий или ответ (parentId). */
export function addComment(id, text, parentId = null) {
  return request(`${P}/${id}/comments`, {
    method: "POST",
    body: { text, parent_id: parentId },
    auth: true,
  });
}

/** Лайк комментария. */
export function likeComment(cid) {
  return request(`${P}/comments/${cid}/like`, { method: "POST", auth: true });
}

/** Отметить раздел прочитанным (сбрасывает бейдж). */
export function markRead() {
  return request(`${P}/mark-read`, { method: "POST", auth: true });
}

/* ── Автор (только МИН id=1) ─────────────────────────────────────── */

export function createNews(payload) {
  return request(`${P}/admin/news`, { method: "POST", body: payload, auth: true });
}

export function updateNews(id, payload) {
  return request(`${P}/admin/news/${id}`, { method: "PUT", body: payload, auth: true });
}

export function deleteNews(id) {
  return request(`${P}/admin/news/${id}`, { method: "DELETE", auth: true });
}

export function pinNews(id) {
  return request(`${P}/admin/news/${id}/pin`, { method: "POST", auth: true });
}

/** Загрузка картинки (multipart). Возвращает {url, full_url}. */
export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("image", file);
  const token = (await import("./client")).getToken?.();
  const res = await fetch(`${API_BASE}${P}/admin/upload-image`, {
    method: "POST",
    headers: token ? { Authorization: token } : {},
    body: fd,
  });
  if (!res.ok) throw new Error("upload_failed");
  return res.json();
}

export function hideComment(cid, reason = "") {
  return request(`${P}/admin/comments/${cid}/hide`, {
    method: "POST",
    body: { reason },
    auth: true,
  });
}

/** URL картинки новости (обложка/блок). */
export function newsImageUrl(filename) {
  if (!filename) return null;
  if (/^https?:\/\//.test(filename)) return filename;
  return `${API_BASE}/api-storys/assets/news/${filename}`;
}

/**
 * Формат «ЧЧ:ММ - ДД.ММ.ГГГГ» в локальном времени браузера пользователя.
 * Бэкенд шлёт ISO с суффиксом 'Z' (UTC) → new Date() корректно сдвигает в локаль.
 */
export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const date = d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${time} - ${date}`;
}

export const CATEGORIES = [
  { id: "update", label: "Обновление", emoji: "🛠" },
  { id: "thoughts", label: "Мысли", emoji: "💭" },
  { id: "announce", label: "Анонс", emoji: "📢" },
];

export const REACTIONS = [
  { id: "like", emoji: "👍" },
  { id: "heart", emoji: "❤️" },
  { id: "fire", emoji: "🔥" },
  { id: "party", emoji: "🎉" },
];
